import { memo, useEffect, useMemo, useRef, useState } from "react"

import { formatDistanceToNow } from "date-fns"
import {
  LayoutDashboard,
  MoreVertical,
  Pencil,
  Star,
  StarOff,
  Trash2,
  User,
  Users,
} from "@/components/icons"
import { useTranslation } from "react-i18next"

import type { DashboardSpec } from "../dashboard/catalog"
import type { SavedDashboard } from "../services/dashboard-service"

import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useDateLocale } from "@/hooks/use-date-locale"
import { cn } from "@/lib/utils"

import { DashboardMiniPreview } from "./dashboard-mini-preview"
import { usePrefetchDashboard } from "../hooks/use-dashboards"

const HOVER_DELAY_MS = 100

type DashboardTileProps = {
  dashboard: SavedDashboard
  onOpen: (dashboard: SavedDashboard) => void
  onEdit: (dashboard: SavedDashboard) => void
  onDelete: (dashboard: SavedDashboard) => void
  onToggleFavorite: (dashboard: SavedDashboard) => void
}

function DashboardTileInner({
  dashboard,
  onOpen,
  onEdit,
  onDelete,
  onToggleFavorite,
}: DashboardTileProps) {
  const { t } = useTranslation()
  const dateLocale = useDateLocale()
  const prefetchDashboard = usePrefetchDashboard()
  const prefetchTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const isCardExpanded = isHovered || isDropdownOpen

  useEffect(() => {
    return () => {
      if (prefetchTimeoutRef.current) {
        clearTimeout(prefetchTimeoutRef.current)
      }
    }
  }, [])

  const handleMouseEnter = () => {
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current)
    }
    prefetchTimeoutRef.current = setTimeout(() => {
      setIsHovered(true)
      prefetchDashboard(dashboard.id)
      prefetchTimeoutRef.current = null
    }, HOVER_DELAY_MS)
  }

  const handleMouseLeave = () => {
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current)
      prefetchTimeoutRef.current = null
    }
    if (!isDropdownOpen) {
      setIsHovered(false)
    }
  }

  useEffect(() => {
    if (isDropdownOpen) {
      if (prefetchTimeoutRef.current) {
        clearTimeout(prefetchTimeoutRef.current)
        prefetchTimeoutRef.current = null
      }
      setIsHovered(true)
    }
  }, [isDropdownOpen])

  const updatedDate = dashboard.updated || dashboard.created
  const relativeTime = updatedDate
    ? formatDistanceToNow(new Date(updatedDate), {
        addSuffix: true,
        locale: dateLocale,
      })
    : ""

  const isPersonal = dashboard.type === "personal"
  const VisibilityIcon = isPersonal ? User : Users
  const visibilityLabel = isPersonal
    ? t("dash.landing.personal_badge")
    : t("dash.landing.company_badge")

  // Count spec elements for summary
  const specSummary = useMemo(() => {
    const spec = dashboard.spec as Record<string, unknown>
    if (!Array.isArray(spec.children)) return null
    const children = spec.children as Array<{ type?: string }>
    const kpis = children.filter((c) => c.type === "KPICard").length
    const charts = children.filter((c) =>
      ["BarChart", "LineChart", "AreaChart", "PieChart"].includes(c.type ?? "")
    ).length
    const tables = children.filter((c) => c.type === "Table").length
    const parts: string[] = []
    if (kpis > 0) parts.push(`${kpis} KPI${kpis > 1 ? "s" : ""}`)
    if (charts > 0) parts.push(`${charts} chart${charts > 1 ? "s" : ""}`)
    if (tables > 0) parts.push(`${tables} table${tables > 1 ? "s" : ""}`)
    return parts.length > 0 ? parts.join(", ") : null
  }, [dashboard.spec])

  const hasSpecContent = useMemo(() => Object.keys(dashboard.spec).length > 0, [dashboard.spec])

  const cardContent = (
    <>
      <div className="flex items-start justify-between gap-2">
        <Badge className="gap-1.5 px-2 py-1 text-[11px]" variant="outline">
          <VisibilityIcon className="h-3 w-3" />
          <span>{visibilityLabel}</span>
        </Badge>

        <div className="flex items-center gap-1">
          <button
            className="bg-card text-muted-foreground hover:border-warning hover:text-warning focus-visible:ring-ring rounded-full border p-1.5 transition focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
            type="button"
            aria-label={`${
              dashboard.is_favorite
                ? t("dash.landing.remove_favorite")
                : t("dash.landing.mark_as_favorite")
            }: ${dashboard.title || t("dash.dashboard.untitled")}`}
            onClick={(e) => {
              e.stopPropagation()
              onToggleFavorite(dashboard)
            }}
          >
            {dashboard.is_favorite ? (
              <Star className="fill-warning text-warning h-4 w-4" />
            ) : (
              <StarOff className="h-4 w-4" />
            )}
          </button>

          <DropdownMenu onOpenChange={setIsDropdownOpen}>
            <DropdownMenuTrigger asChild>
              <button
                aria-label={`${t("dash.landing.more_actions")}: ${dashboard.title || t("dash.dashboard.untitled")}`}
                className="bg-card text-muted-foreground hover:bg-accent focus-visible:ring-ring rounded-full border p-1.5 transition focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
                type="button"
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  onEdit(dashboard)
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                {t("dash.landing.edit")}
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onClick={(e) => {
                  e.stopPropagation()
                  onDelete(dashboard)
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t("dash.landing.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div
        className={cn(
          "overflow-hidden transition-[height,margin] duration-300 ease-out",
          isCardExpanded && hasSpecContent ? "my-2 h-[140px]" : "h-0"
        )}
      >
        {isCardExpanded && hasSpecContent && (
          <DashboardMiniPreview
            className="bg-muted/30 h-[140px] w-full scale-100 rounded-lg border opacity-100 transition-[height] duration-300 ease-out"
            spec={dashboard.spec as DashboardSpec}
          />
        )}
      </div>

      <h3 className="mt-2 text-base leading-tight font-semibold">
        <button
          className="focus-visible:ring-ring rounded-sm text-left focus-visible:ring-2 focus-visible:outline-none focus-visible:ring-inset"
          title={t("dash.landing.open_dashboard")}
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            onOpen(dashboard)
          }}
        >
          {dashboard.title || t("dash.dashboard.untitled")}
        </button>
      </h3>

      {dashboard.description && (
        <p className={cn("text-muted-foreground mt-1 text-sm", !isCardExpanded && "line-clamp-2")}>
          {dashboard.description}
        </p>
      )}

      {isCardExpanded && specSummary && (
        <div className="mt-2 flex items-center gap-1.5">
          <LayoutDashboard className="text-muted-foreground h-3.5 w-3.5" />
          <span className="text-muted-foreground text-xs">{specSummary}</span>
        </div>
      )}

      <div className="border-border mt-auto border-t pt-2">
        <p className="text-muted-foreground text-xs">
          {t("dash.dashboard.last_updated", { time: relativeTime })}
        </p>
      </div>
    </>
  )

  return (
    <div
      className="relative"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onBlur={(e) => {
        // Focus moving to another control inside the tile is not leaving it;
        // collapsing synchronously here would re-layout mid-Tab.
        if (e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget)) return
        if (!isDropdownOpen) {
          setIsHovered(false)
        }
      }}
      onFocus={() => {
        setIsHovered(true)
      }}
    >
      {/* Invisible placeholder to maintain layout space */}
      <div
        aria-hidden="true"
        inert
        className="pointer-events-none invisible flex flex-col gap-2 p-4"
      >
        {cardContent}
      </div>

      <Card
        role="presentation"
        className={cn(
          "group absolute flex cursor-pointer flex-col gap-2 p-4 transition-[border-color,box-shadow,inset,top,left,right] duration-300",
          "hover:border-primary/50",
          isCardExpanded
            ? "-inset-x-2 -top-4 z-20 shadow-2xl max-sm:max-w-[calc(100vw-2rem)] sm:-inset-x-4"
            : "inset-0 overflow-hidden"
        )}
        onClick={() => onOpen(dashboard)}
      >
        {cardContent}
      </Card>
    </div>
  )
}

export const DashboardTile = memo(DashboardTileInner)

type DashboardTileGridProps = {
  items: SavedDashboard[]
  emptyMessage?: string
  onOpen: (dashboard: SavedDashboard) => void
  onEdit: (dashboard: SavedDashboard) => void
  onDelete: (dashboard: SavedDashboard) => void
  onToggleFavorite: (dashboard: SavedDashboard) => void
}

export function DashboardTileGrid({
  items,
  emptyMessage,
  onOpen,
  onEdit,
  onDelete,
  onToggleFavorite,
}: DashboardTileGridProps) {
  const { t } = useTranslation()

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <LayoutDashboard className="text-muted-foreground size-12" />
        <p className="text-muted-foreground text-sm">
          {emptyMessage ?? t("dash.landing.no_dashboards")}
        </p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((dashboard) => (
        <DashboardTile
          key={dashboard.id}
          dashboard={dashboard}
          onDelete={onDelete}
          onEdit={onEdit}
          onOpen={onOpen}
          onToggleFavorite={onToggleFavorite}
        />
      ))}
    </div>
  )
}
